import { Action, combineReducers, configureStore, ThunkAction } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";

import { appReducer } from "./app/app.slice";
import { batchApi } from "./batch/batch.api";
import { categoryApi } from "./category/category.api";
import { dashboardApi } from "./dashboard/dashboard.api";
import { requestApi } from "./request/request.api";
import { authReducer } from "./auth/auth.slice";
import { memberApi } from "./member/member.api";
import { authApi } from "./auth/auth.api";
import { activityApi } from "./activity/activity.api";
import { studentApi } from "./student/student.api";
import { fileApi } from "./file/file.api";
import { announcementApi } from "./announcement/annoucement.api";
import { paymentApi } from "./payment/payment.api";
import { cartApi } from "./cart/cart.api";
import { cartOrderApi } from "./cartOrder/cartOrder.api";
import { orderApi } from "./order/order.api";
import { contentApi } from "./content/content.api";
import { attendanceApi } from "./attendance/attendance.api";
import { performanceApi } from "./performance/performance.api";
import { studentTestResultApi } from "./studentTestResult/studentTestResult.api";
import { fulfilmentApi } from "./fulfilment/fulfilment.api";
import { manifestApi } from "./manifest/manifest.api";
import { shipmentApi } from "./shipment/shipment.api";
import { shippingApi } from "./shipping/shipping.api";
import { questionApi } from "./question/question.api";
import { testApi } from "./test/test.api";
import { assignmentApi } from "./assignment/assignment.api";
import { subjectApi } from "./subject/subject.api";
import { chapterApi } from "./chapter/chapter.api";
import { tagApi } from "./tag/tag.api";
import { articleApi } from "./article/article.api";
import { visitorApi } from "./visitor/visitor.api";
import { landingApi } from "./landing/landing.api";
import { salesReportApi } from "./payment/salesReport.api";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";
import { chatApi } from "./chat/chat.api";
import { accessApi } from "./app/access.api";
import { invoiceApi } from "./invoice/invoice.api";

const createNoopStorage = () => ({
  getItem: (_key: string) => Promise.resolve(null),
  setItem: (_key: string, value: string) => Promise.resolve(),
  removeItem: (_key: string) => Promise.resolve(),
});

const storage = typeof window !== "undefined" ? createWebStorage("local") : createNoopStorage();

const persistConfig = {
  key: "CHESTA_AUTH",
  storage,
};

const rootReducer = combineReducers({
  app: appReducer,
  auth: persistReducer(persistConfig, authReducer),
  [requestApi.reducerPath]: requestApi.reducer,
  [categoryApi.reducerPath]: categoryApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [batchApi.reducerPath]: batchApi.reducer,
  [studentApi.reducerPath]: studentApi.reducer,
  [memberApi.reducerPath]: memberApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [activityApi.reducerPath]: activityApi.reducer,
  [fileApi.reducerPath]: fileApi.reducer,
  [announcementApi.reducerPath]: announcementApi.reducer,
  [testApi.reducerPath]: testApi.reducer,
  [assignmentApi.reducerPath]: assignmentApi.reducer,
  [paymentApi.reducerPath]: paymentApi.reducer,
  [cartApi.reducerPath]: cartApi.reducer,
  [cartOrderApi.reducerPath]: cartOrderApi.reducer,
  [orderApi.reducerPath]: orderApi.reducer,
  [contentApi.reducerPath]: contentApi.reducer,
  [attendanceApi.reducerPath]: attendanceApi.reducer,
  [performanceApi.reducerPath]: performanceApi.reducer,
  [studentTestResultApi.reducerPath]: studentTestResultApi.reducer,
  [fulfilmentApi.reducerPath]: fulfilmentApi.reducer,
  [manifestApi.reducerPath]: manifestApi.reducer,
  [shipmentApi.reducerPath]: shipmentApi.reducer,
  [shippingApi.reducerPath]: shippingApi.reducer,
  [questionApi.reducerPath]: questionApi.reducer,
  [subjectApi.reducerPath]: subjectApi.reducer,
  [chapterApi.reducerPath]: chapterApi.reducer,
  [tagApi.reducerPath]: tagApi.reducer,
  [articleApi.reducerPath]: articleApi.reducer,
  [visitorApi.reducerPath]: visitorApi.reducer,
  [landingApi.reducerPath]: landingApi.reducer,
  [chatApi.reducerPath]: chatApi.reducer,
  [salesReportApi.reducerPath]: salesReportApi.reducer,
  [accessApi.reducerPath]: accessApi.reducer,
  [invoiceApi.reducerPath]: invoiceApi.reducer,
});

export interface AnyAction extends Action {
  // Allows any extra properties to be defined in an action.
  [extraProps: string]: any;
}

const mainReducer = (rootState: RootReducerType | undefined, action: AnyAction) => {
  let state = rootState;
  if (action.type === "authApi/executeMutation/pending") {
    if (action.meta.arg.endpointName === "logout") {
      state = undefined;
      storage.removeItem(`persist:${persistConfig.key}`);
      localStorage.clear();
    }
  }

  return rootReducer(state, action);
};

const store = configureStore({
  reducer: mainReducer,
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false })
      .concat(requestApi.middleware)
      .concat(categoryApi.middleware)
      .concat(dashboardApi.middleware)
      .concat(batchApi.middleware)
      .concat(memberApi.middleware)
      .concat(authApi.middleware)
      .concat(activityApi.middleware)
      .concat(studentApi.middleware)
      .concat(fileApi.middleware)
      .concat(announcementApi.middleware)
      .concat(testApi.middleware)
      .concat(assignmentApi.middleware)
      .concat(paymentApi.middleware)
      .concat(cartApi.middleware)
      .concat(cartOrderApi.middleware)
      .concat(orderApi.middleware)
      .concat(contentApi.middleware)
      .concat(attendanceApi.middleware)
      .concat(performanceApi.middleware)
      .concat(studentTestResultApi.middleware)
      .concat(fulfilmentApi.middleware)
      .concat(manifestApi.middleware)
      .concat(shipmentApi.middleware)
      .concat(shippingApi.middleware)
      .concat(questionApi.middleware)
      .concat(subjectApi.middleware)
      .concat(chapterApi.middleware)
      .concat(tagApi.middleware)
      .concat(articleApi.middleware)
      .concat(visitorApi.middleware)
      .concat(landingApi.middleware)
      .concat(chatApi.middleware)
      .concat(salesReportApi.middleware)
      .concat(accessApi.middleware)
      .concat(invoiceApi.middleware)
});

export type RootReducerType = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;

export const persistor = persistStore(store);
export default store;
